import { Link, useNavigate, useParams } from 'react-router';
import { api, formatDate } from '../api.ts';
import { useAuth } from '../auth.tsx';
import { Container } from '../components/Layout.tsx';
import { Alert, Button, Card, ErrorMessage, Loading, PageTitle } from '../components/ui.tsx';
import { useAction, useApi, useTitle } from '../hooks.ts';

interface JoinInfo {
  courseTitle: string;
  cohortName: string;
  startsOn: string | null;
  endsOn: string | null;
  open: boolean;
  enrolled: boolean;
}

/** Anmälan till ett (ofta dolt) kurstillfälle via den hemliga länken /anmal/<token>. */
export function JoinPage() {
  useTitle('Anmälan');
  const { token = '' } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, error, loading } = useApi<JoinInfo>(`/api/join/${encodeURIComponent(token)}`);
  const join = useAction(() => api.post(`/api/join/${encodeURIComponent(token)}`));
  const here = `/anmal/${token}`;

  return (
    <Container narrow>
      <PageTitle>Anmälan</PageTitle>
      <Card className="space-y-4">
        {loading && !data && <Loading />}
        {error && (
          <Alert>
            Länken gäller inte. Kontrollera att du fått hela adressen, eller fråga kursens lärare.
          </Alert>
        )}
        {data && (
          <>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-muted">Kurstillfälle</p>
              <h2 className="text-xl font-bold text-night-900">{data.courseTitle}</h2>
              <p className="text-muted">
                {data.cohortName}
                {data.startsOn && ` · från ${formatDate(data.startsOn)}`}
                {data.endsOn && ` till ${formatDate(data.endsOn)}`}
              </p>
            </div>
            {data.enrolled ? (
              <>
                <Alert kind="success">Du är anmäld till det här kurstillfället.</Alert>
                <Link to="/kurs" className="inline-block font-semibold text-night-700 underline">
                  Till kursen
                </Link>
              </>
            ) : !data.open ? (
              <Alert>Anmälan är stängd just nu. Kontakta kursens lärare.</Alert>
            ) : user ? (
              <>
                <p>
                  Du är inloggad som <strong>{user.email}</strong>. Anmäl dig så hamnar du direkt i det här
                  kurstillfället.
                </p>
                <ErrorMessage error={join.error} />
                <Button
                  pending={join.pending}
                  onClick={async () => {
                    if (await join.run()) navigate('/kurs');
                  }}
                >
                  Anmäl mig
                </Button>
              </>
            ) : (
              <>
                <p>Skapa ett konto för att anmäla dig, eller logga in om du redan har ett.</p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/registrera?kurs=${encodeURIComponent(token)}`}
                    className="rounded-lg bg-night-800 px-4 py-2.5 font-semibold text-white hover:bg-night-900"
                  >
                    Skapa konto och anmäl mig
                  </Link>
                  <Link
                    to={`/logga-in?till=${encodeURIComponent(here)}`}
                    className="rounded-lg px-4 py-2.5 font-semibold text-night-800 ring-1 ring-night-800/20 hover:bg-night-50"
                  >
                    Logga in
                  </Link>
                </div>
              </>
            )}
          </>
        )}
      </Card>
    </Container>
  );
}
